import React, { useEffect, useState } from 'react';
import { Divider, Form, Grid, Header, Message } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';

/**
 * AlphaQ 智能路由(alphaqauto)配置面板 —— 后台可视化管理招牌自动模型的全部切换策略。
 *
 * 所有项均走 one-api option 热更(PUT /api/option), 保存即时生效, 无需重启:
 *   · 总开关(即时切换): 智能入口 / 难度路由 / 能力(模态)路由 / 恒用最强 / 超难档
 *   · 难度三档候选链(易/中/难) + 难度阈值
 *   · 超难档(Grok 多智能体)候选链 + 触发阈值
 *   · 恒用最强 / 快速 候选链 + 长短阈值
 *   · 视觉候选链(含图片请求)
 *   · 限频冷却秒数(模型限频后冷却, 预选跳过)
 *   · 应急强制锁定(止血开关: 纯文本请求绕过智能路由直走指定模型)
 *
 * 候选链均为【逗号分隔模型列表】: 第一个为首选, 其后为 failover 备胎; 某个受限(限频/额度/5xx)自动切下一个。
 */
const AutoRoutingSetting = () => {
  // 全部智能路由相关 option 键(字符串态; 布尔以 'true'/'false' 存)。
  let [inputs, setInputs] = useState({
    AutoModelEnabled: '',
    AutoModelName: '',
    AutoDifficultyRoutingEnabled: '',
    AutoCapabilityRoutingEnabled: '',
    AutoAlwaysStrongEnabled: '',
    AutoUltraRoutingEnabled: '',
    AutoEasyModel: '',
    AutoMediumModel: '',
    AutoHardModel: '',
    AutoEasyMaxTokens: '',
    AutoHardMinTokens: '',
    AutoUltraModel: '',
    AutoUltraMinTokens: '',
    AutoStrongModel: '',
    AutoFastModel: '',
    AutoLongThreshold: '',
    AutoVisionModel: '',
    AutoRateLimitCooldownSeconds: '',
    AutoPrimaryModelEnabled: '',
    AutoPrimaryModel: '',
    AutoForceModelEnabled: '',
    AutoForceModel: '',
  });
  const [originInputs, setOriginInputs] = useState({});
  let [loading, setLoading] = useState(false);
  // 可选模型下拉项: 取自【当前启用渠道】实际配置的模型并集(= 目前真正支持的模型), 不允许手填。
  const [modelOptions, setModelOptions] = useState([]);

  const getOptions = async () => {
    const res = await API.get('/api/option/');
    const { success, message, data } = res.data;
    if (success) {
      let newInputs = {};
      data.forEach((item) => {
        newInputs[item.key] = item.value;
      });
      setInputs((inputs) => ({ ...inputs, ...newInputs }));
      setOriginInputs(newInputs);
    } else {
      showError(message);
    }
  };

  // 拉取"目前支持的模型"作下拉项: 遍历渠道总览, 只取【已启用】(status===1)渠道上配置的模型, 去重排序。
  // 这样下拉里出现的都是网关当前真能调通的模型, 避免手填错模型名导致路由到不存在的模型。
  const getModelOptions = async () => {
    try {
      const res = await API.get('/api/channel/overview');
      const { success, data } = res.data;
      if (!success || !Array.isArray(data)) return;
      const set = new Set();
      data.forEach((ch) => {
        if (ch.status !== 1) return; // 仅启用渠道
        (ch.models || []).forEach((m) => {
          if (m) set.add(m);
        });
      });
      const opts = Array.from(set)
        .sort()
        .map((m) => ({ key: m, text: m, value: m }));
      setModelOptions(opts);
    } catch (e) {
      // 拉取失败不阻断设置页其余功能, 下拉为空时提示用户去渠道页确认
      showError('可选模型列表加载失败, 请确认渠道已配置');
    }
  };

  useEffect(() => {
    getOptions().then();
    getModelOptions().then();
  }, []);

  // 单项热更: *Enabled 键为切换(忽略传入值, 依当前值翻转); 其余按传入值写。
  const updateOption = async (key, value) => {
    setLoading(true);
    if (key.endsWith('Enabled')) {
      value = inputs[key] === 'true' ? 'false' : 'true';
    }
    const res = await API.put('/api/option/', { key, value });
    const { success, message } = res.data;
    if (success) {
      setInputs((inputs) => ({ ...inputs, [key]: value }));
      setOriginInputs((o) => ({ ...o, [key]: value }));
      showSuccess('已保存并即时生效');
    } else {
      showError(message);
    }
    setLoading(false);
  };

  // 文本/数值项: 仅缓冲到 state, 由分组"保存"按钮统一提交; 开关项即时提交。
  const handleInputChange = async (e, { name, value }) => {
    if (name.endsWith('Enabled')) {
      await updateOption(name, value);
    } else {
      setInputs((inputs) => ({ ...inputs, [name]: value }));
    }
  };

  // 分组保存: 仅提交该组内相对 origin 有变化的键, 避免无谓写库。
  const submitGroup = async (keys) => {
    let changed = keys.filter((k) => originInputs[k] !== inputs[k]);
    if (changed.length === 0) {
      showSuccess('无改动');
      return;
    }
    for (const k of changed) {
      await updateOption(k, inputs[k]);
    }
  };

  return (
    <Grid columns={1}>
      <Grid.Column>
        <Form loading={loading}>
          <Header as='h3'>AlphaQ 智能路由 · alphaqauto</Header>
          <Message size='tiny' info>
            招牌自动模型 <b>alphaqauto</b> 的全部切换策略。候选链为逗号分隔的模型列表(第一个首选,
            其后为 failover 备胎), 某模型受限(限频/额度/5xx)自动切下一个。所有改动保存即时热更生效。
          </Message>

          {/* —— 总开关(即时生效)—— */}
          <Header as='h4'>总开关(点击即时生效)</Header>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.AutoModelEnabled === 'true'}
              label='启用智能入口(alphaqauto 生效; 关闭则按原样透传)'
              name='AutoModelEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.AutoDifficultyRoutingEnabled === 'true'}
              label='难度分档路由(按任务难度切 易/中/难)'
              name='AutoDifficultyRoutingEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.AutoCapabilityRoutingEnabled === 'true'}
              label='能力(模态)路由(含图片→视觉模型; 含音频→ASR)'
              name='AutoCapabilityRoutingEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.AutoUltraRoutingEnabled === 'true'}
              label='超难档(超长任务→Grok 多智能体并行)'
              name='AutoUltraRoutingEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.AutoAlwaysStrongEnabled === 'true'}
              label='恒用最强(难度路由关闭时: 一律走最强模型)'
              name='AutoAlwaysStrongEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>

          <Divider />
          {/* —— 初始指定模型(优先模型)—— */}
          <Header as='h4'>初始指定模型(优先使用)</Header>
          <Message size='tiny' info>
            开启后, 每次调用 <b>alphaqauto</b> 都<b>优先使用</b>下方指定的模型; 一旦该模型
            <b>限额 / 限频 / 请求失败</b>, 立即自动切换到其它可用模型(原本的难度智能链自动成为它的备胎),
            用户请求不中断。<b>关闭则完全回归「按任务难度智能切换」</b>。
            含图片/音频的请求不受影响(仍走视觉/语音专用模型)。
          </Message>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.AutoPrimaryModelEnabled === 'true'}
              label='启用初始指定模型'
              name='AutoPrimaryModelEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Select
              label='指定模型(下拉选择, 取自当前已启用渠道的模型)'
              name='AutoPrimaryModel'
              search
              clearable
              options={modelOptions}
              value={inputs.AutoPrimaryModel}
              placeholder={
                modelOptions.length
                  ? '请选择模型'
                  : '暂无可选模型(请先在渠道页配置并启用渠道)'
              }
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button onClick={() => submitGroup(['AutoPrimaryModel'])}>
            保存 指定模型
          </Form.Button>

          <Divider />
          {/* —— 触发名 —— */}
          <Header as='h4'>入口触发名</Header>
          <Form.Group widths='equal'>
            <Form.Input
              label='触发名(逗号分隔多别名, 含招牌名 alphaqauto)'
              name='AutoModelName'
              value={inputs.AutoModelName}
              placeholder='alphaqauto,auto'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button onClick={() => submitGroup(['AutoModelName'])}>
            保存 触发名
          </Form.Button>

          <Divider />
          {/* —— 难度三档候选链 —— */}
          <Header as='h4'>难度三档候选链(文本/编程)</Header>
          <Form.Input
            label='易档(短/简单任务, 快而省)'
            name='AutoEasyModel'
            value={inputs.AutoEasyModel}
            placeholder='deepseek-v4-flash,mimo-v2.5,doubao-seed-2.0-lite'
            onChange={handleInputChange}
          />
          <Form.Input
            label='中档(中等任务)'
            name='AutoMediumModel'
            value={inputs.AutoMediumModel}
            placeholder='glm-5.2,minimax-m3,LongCat-2.0,deepseek-v4-pro,grok-4.5'
            onChange={handleInputChange}
          />
          <Form.Input
            label='难档(复杂/长任务, 最强)'
            name='AutoHardModel'
            value={inputs.AutoHardModel}
            placeholder='k3,deepseek-v4-pro,minimax-m3,grok-4.5,LongCat-2.0'
            onChange={handleInputChange}
          />
          <Form.Group widths='equal'>
            <Form.Input
              label='易档上限 token(< 此值且无代码 判易)'
              name='AutoEasyMaxTokens'
              type='number'
              value={inputs.AutoEasyMaxTokens}
              onChange={handleInputChange}
            />
            <Form.Input
              label='难档起始 token(>= 此值 判难; 之间为中)'
              name='AutoHardMinTokens'
              type='number'
              value={inputs.AutoHardMinTokens}
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button
            onClick={() =>
              submitGroup([
                'AutoEasyModel',
                'AutoMediumModel',
                'AutoHardModel',
                'AutoEasyMaxTokens',
                'AutoHardMinTokens',
              ])
            }
          >
            保存 难度三档
          </Form.Button>

          <Divider />
          {/* —— 超难档 —— */}
          <Header as='h4'>超难档(Grok 多智能体并行)</Header>
          <Form.Group widths='equal'>
            <Form.Input
              label='超难档候选链'
              name='AutoUltraModel'
              value={inputs.AutoUltraModel}
              placeholder='grok-4.20-multi-agent-0309,k3'
              onChange={handleInputChange}
            />
            <Form.Input
              label='超难触发 token(prompt >= 此值判超难)'
              name='AutoUltraMinTokens'
              type='number'
              value={inputs.AutoUltraMinTokens}
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button
            onClick={() => submitGroup(['AutoUltraModel', 'AutoUltraMinTokens'])}
          >
            保存 超难档
          </Form.Button>

          <Divider />
          {/* —— 最强/快速档 + 视觉 —— */}
          <Header as='h4'>最强/快速档 + 视觉候选链</Header>
          <Form.Group widths='equal'>
            <Form.Input
              label='最强档(恒用最强 / 长请求)'
              name='AutoStrongModel'
              value={inputs.AutoStrongModel}
              placeholder='deepseek-v4-pro,k3,minimax-m3'
              onChange={handleInputChange}
            />
            <Form.Input
              label='快速档(短请求省钱)'
              name='AutoFastModel'
              value={inputs.AutoFastModel}
              placeholder='deepseek-v4-flash,mimo-v2.5'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input
              label='长/短请求阈值 token'
              name='AutoLongThreshold'
              type='number'
              value={inputs.AutoLongThreshold}
              onChange={handleInputChange}
            />
            <Form.Input
              label='视觉候选链(含图片请求)'
              name='AutoVisionModel'
              value={inputs.AutoVisionModel}
              placeholder='k3,doubao-seed-2.0-pro,minimax-m3'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button
            onClick={() =>
              submitGroup([
                'AutoStrongModel',
                'AutoFastModel',
                'AutoLongThreshold',
                'AutoVisionModel',
              ])
            }
          >
            保存 最强/快速/视觉
          </Form.Button>

          <Divider />
          {/* —— 限频冷却 —— */}
          <Header as='h4'>限频冷却(熔断)</Header>
          <Message size='tiny'>
            某模型被限频/额度拒绝后, 冷却指定秒数; 冷却期内新请求预选阶段主动跳过它, 从下一个可用候选起步,
            消除"每请求先撞一次限频模型"的延迟税。设 <b>0</b> 关闭(退回撞了才切)。
          </Message>
          <Form.Group widths='equal'>
            <Form.Input
              label='冷却秒数(0=关闭)'
              name='AutoRateLimitCooldownSeconds'
              type='number'
              value={inputs.AutoRateLimitCooldownSeconds}
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button
            onClick={() => submitGroup(['AutoRateLimitCooldownSeconds'])}
          >
            保存 限频冷却
          </Form.Button>

          <Divider />
          {/* —— 应急强制锁定 —— */}
          <Header as='h4'>应急强制锁定(止血开关)</Header>
          <Message size='tiny' warning>
            开启后, 主入口 <b>alphaqauto</b> 的纯文本/编程请求<b>绕过智能路由</b>, 直接走下方指定模型
            (逗号分隔候选, 仍享 failover/冷却)。含图片/音频请求不受影响仍走视觉/ASR。用于某模型集体故障时应急,
            <b>平时请关闭</b>。
            <br />
            <b>与上面「初始指定模型」的区别</b>: 初始指定 = <i>优先用它, 失败自动回落难度智能链</i>(日常);
            应急锁定 = <i>只走锁定模型, 不回落智能链</i>(止血)。两者同时开启时以<b>应急锁定</b>优先。
          </Message>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.AutoForceModelEnabled === 'true'}
              label='启用应急强制锁定'
              name='AutoForceModelEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input
              label='锁定目标模型(逗号分隔候选; 空=不生效)'
              name='AutoForceModel'
              value={inputs.AutoForceModel}
              placeholder='grok-4.5,k3'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button onClick={() => submitGroup(['AutoForceModel'])}>
            保存 锁定目标
          </Form.Button>
        </Form>
      </Grid.Column>
    </Grid>
  );
};

export default AutoRoutingSetting;
